// 1. Añade useRef a los imports
// eslint-disable-next-line import/no-extraneous-dependencies
import React, { useCallback, useRef } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress, Alert,
} from 'reactstrap';
import { useAssetExtractor } from './useAssetExtractor';
import styles from './styles.module.scss';

const AutomaticAssetCreator = (props) => {
  const {
    isOpen,
    toggle,
    className,
    onStartProcessing,
  } = props;

  const show = Boolean(typeof isOpen !== 'undefined' ? isOpen : props.open);

  // 2. Crea la referencia para el input oculto
  const fileInputRef = useRef(null);

  const {
    status,
    progress,
    message,
    error,
    duration,
    processFile,
    reset,
    createdAssetId,
  } = useAssetExtractor(onStartProcessing);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    if (status !== 'idle') return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  }, [status, processFile]);

  const onDragOver = (e) => { e.preventDefault(); };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const handleClose = () => {
    if (status === 'processing' || status === 'uploading') return;
    reset();
    toggle();
  };

  // 3. Modifica la función de abrir el input
  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const renderBodyContent = () => {
    if (status === 'idle' || status === 'negotiating' || status === 'uploading') {
      const isBusy = status !== 'idle';
      return (
        <div
          className={`${styles.dropZone} ${isBusy ? styles.disabled : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onClick={() => !isBusy && openFileDialog()}
        >
          {/* Vinculamos el ref aquí */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".pdf,.xlsx,.xlsm"
            onChange={handleFileSelect}
            disabled={isBusy}
          />

          {!isBusy ? (
            <div className={styles.textContainer}>
              <div className={styles.iconContainer}><i className="fa fa-upload" /></div>
              <p className="mb-0">
                Drag your file (
                <strong>.pdf</strong>
                {' '}
                or
                <strong>.xlsx</strong>
                ) here
                <br />
                {' '}
                or click to select
              </p>
            </div>
          ) : (
            <div className={styles.spinnerContainer}>
              <i className="fa fa-spinner fa-spin" />
              <p>{message || 'Uploading file...'}</p>
            </div>
          )}
        </div>
      );
    }
    // ... el resto del código sigue igual ...
    if (status === 'processing') {
      return (
        <div className={styles.progressSuccessContainer}>
          <div className="mb-3 w-100">
            <h6 className="text-primary font-weight-bold mb-1">Processing Asset...</h6>
            <p className={styles.progressInfoText}>{message || 'Please wait'}</p>
          </div>
          <div className="w-100 px-3">
            <Progress value={progress} color="primary" animated className="mb-2" style={{ height: '10px' }} />
            <div className="d-flex justify-content-end">
              <small className={styles.progressInfoText}>
                {progress.toFixed(0)}
                %
              </small>
            </div>
          </div>
        </div>
      );
    }
    if (status === 'success') {
      return (
        <div className={styles.progressSuccessContainer}>
          <div className="alert alert-success py-3 w-80 text-center">
            <h5 className="alert-heading mb-1 md">
              <i className="fa fa-check-circle fa-sm" />
              Completed
            </h5>
            <p className="mb-0 small">The asset has been created.</p>
          </div>
          <div className="d-flex justify-content-center mt-4 w-100">
            {createdAssetId && (
            <Button color="success" className={`mr-2 ${styles.btnConfirm}`} onClick={() => window.location.href = `#/asset/${createdAssetId}`}>
              <i className="fa fa-external-link mr-1" />
              {' '}
              View Asset
            </Button>
            )}
            <Button outline color="secondary" onClick={reset} className={styles.btnOutlineSecondary}>Create New Asset</Button>
          </div>
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div className={styles.progressSuccessContainer}>
          <Alert color="danger" className="text-left w-100 py-3">
            <strong>
              <i className="fa fa-times-circle" />
              {' '}
              Error:
            </strong>
            {' '}
            {error || 'An unknown error occurred'}
          </Alert>
          <Button size="sm" color="secondary" onClick={reset} className={`mt-3 ${styles.btnOutlineSecondary}`}>Try Again</Button>
        </div>
      );
    }
    return null;
  };

  return (
    <Modal isOpen={show} toggle={handleClose} className={`${className} ${styles.modalClean}`} size="md" backdrop={status === 'processing' ? 'static' : true} keyboard={status !== 'processing'}>
      <ModalHeader toggle={handleClose} className={`bg-white py-3 ${styles.customHeaderBorder}`}>
        <div className="d-flex align-items-center w-100 justify-content-between">
          <span className={styles.modalTitle}>
            <i className="fa fa-upload mr-1 text-secondary" />
            {' '}
            Automatic Asset Creator
          </span>
        </div>
      </ModalHeader>
      <ModalBody className={`p-0 ${styles.staticBody}`}>
        {(status !== 'idle') && (
        <span className={styles.timerBadge}>
          <i className="fa fa-clock-o mr-1" />
          {' '}
          {duration}
          s
        </span>
        )}
        {(status === 'idle') && (
        <div className={`${styles.corporateInfoAlert} mx-4 mt-4`}>
          <i className="fa fa-info-circle" />
          <div>Use this modal to upload your asset files.</div>
        </div>
        )}
        {renderBodyContent()}
      </ModalBody>
      {(status === 'idle' || status === 'error') && (
        <ModalFooter className={`pt-3 ${styles.customFooterBorder}`}>
          <Button outline color="secondary" onClick={handleClose} size="sm" style={{ minWidth: '80px' }} className={styles.btnOutlineSecondary}>Cancel</Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

// eslint-disable-next-line react/prop-types
export const AutomaticAssetCreatorButton = ({ onClick, label = 'Auto Creator' }) => (
  <Button className={styles.btnAutoCreator} onClick={onClick}>
    <i className="fa fa-bolt mr-1" />
    {label}
  </Button>
);

export default AutomaticAssetCreator;
